import React, { useState } from "react";
import { Box, Card, CardContent, IconButton, Typography, Button, CircularProgress } from "@mui/material";
import { ArrowBackRounded, RuleOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import MainLayout from "../../../layouts/main/main_layout";
import { RulesCreationForm } from "../../landing_page/components/rules-creation-form";
import { createOrUpdateRule } from "../../../services/landing_page/landing_page";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { black, greyEnabled, purple, purpleLight } from "../../../theme/colors";

export const AddRule: React.FC<{ viewMode: boolean }> = ({ viewMode = false }) => {
  const location = useLocation();
  const ruleData = location.state?.ruleData || null;
  const navigate = useNavigate();
  const { showSnackbar } = useFeedback();
  const queryClient = useQueryClient();
  const [rulesData, setRulesData] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!rulesData) {
      showSnackbar("Please fill in the rule details");
      return;
    }

    setIsSaving(true);
    try {
      await createOrUpdateRule(rulesData);
      showSnackbar("Rule saved successfully!");
      queryClient.invalidateQueries({ queryKey: ["rules"] });
      navigate('/rules');
    } catch (error: any) {
      const errorMsg = error?.response?.data?.error?.message || "Failed to save rule. Please try again.";
      showSnackbar(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/rules');
  };

  // Transform API response to form data structure for editing
  const initialFormData = ruleData ? {
    name: ruleData?.name || null,
    description: ruleData?.description || null,
    content_id: ruleData?.content_id || null,
    ...ruleData?.rules, // Spread all rules fields
  } : null;

  return (
    <MainLayout>
      <Card elevation={0} sx={{ width: "100%", borderRadius: 3, border: "1px solid #ECECEC", m: 1 }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Box display="flex" alignItems="center" gap={1} mb={3}>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackRounded />
            </IconButton>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: purpleLight,
                  flexShrink: 0,
                }}
              >
                <RuleOutlined sx={{ color: purple, fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} color={black}>
                  {viewMode ? "View Rule" : ruleData ? "Edit Rule" : "Add Rule"}
                </Typography>
                <Typography variant="body2" color={greyEnabled}>
                  Define who this content page should be shown to.
                </Typography>
              </Box>
            </Box>
          </Box>

          <RulesCreationForm
            viewMode={viewMode}
            initialData={initialFormData}
            onDataChange={(data) => setRulesData(data)}
            handleSave={handleSave}
            currentRuleId={ruleData?.id || null}
          />

          {/* Save/Cancel buttons at bottom */}
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-start", mt: 4, pt: 3, borderTop: "1px solid #ECECEC" }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isSaving}
              sx={{
                borderColor: purple,
                color: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { borderColor: purple, bgcolor: purpleLight },
              }}
            >
              {viewMode ? "Back" : "Cancel"}
            </Button>
            {!viewMode && (
              <Button
                variant="contained"
                disableElevation
                onClick={handleSave}
                disabled={isSaving}
                startIcon={isSaving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
                sx={{
                  bgcolor: purple,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#7A2074" },
                }}
              >
                {isSaving ? "Saving..." : "Save Rule"}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </MainLayout>
  );
};

